import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { usePropertyInsights } from '@/stores/pattern-store';
import { SimilarDayInsight } from './SimilarDayInsight';
import { BestConditionsInsight } from './BestConditionsInsight';
import { LocationComparisonInsight } from './LocationComparisonInsight';
import type { SimilarDayMatch, BestConditionsSummary, LocationComparison } from '@/types/patterns';

interface PropertyInsightsPanelProps {
  propertyId: string;
  propertyName?: string;
}

function InsightsSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse" data-testid="insights-skeleton">
      <div className="h-3 bg-[var(--color-elevated)] rounded w-48" />
      <div className="h-3 bg-[var(--color-elevated)] rounded w-64" />
      <div className="h-3 bg-[var(--color-elevated)] rounded w-40" />
    </div>
  );
}

export function PropertyInsightsPanel({ propertyId, propertyName }: PropertyInsightsPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const { insights, dataQuality, isLoading } = usePropertyInsights(propertyId, propertyName);

  const similarDayInsights = (insights?.insights ?? []).filter(
    (i): i is SimilarDayMatch => i.type === 'similar_day',
  );
  const bestConditions = (insights?.insights ?? []).find(
    (i): i is BestConditionsSummary => i.type === 'best_conditions',
  );
  const locationComparison = (insights?.insights ?? []).find(
    (i): i is LocationComparison => i.type === 'location_comparison',
  );

  const entriesNeeded = insights ? Math.max(0, 3 - insights.totalEntries) : 3;

  return (
    <section
      className="bg-bark-800 border border-ember-800/50 rounded-lg shadow-ember p-4 sm:p-5"
      data-testid="property-insights-panel"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center justify-between w-full mb-0"
        aria-expanded={expanded}
        data-testid="insights-toggle"
      >
        <p className="field-label">
          Property Insights
        </p>
        {expanded ? (
          <ChevronUp size={14} className="text-[var(--color-text-muted)]" />
        ) : (
          <ChevronDown size={14} className="text-[var(--color-text-muted)]" />
        )}
      </button>

      {expanded && (
        <div className="mt-3">
          {isLoading && !insights && <InsightsSkeleton />}

          {!isLoading && dataQuality === 'insufficient' && (
            // M70: Demoted to compact single-line — no card wrapper, no large empty state
            <p
              className="text-body-xs text-[var(--color-text-muted)]"
              data-testid="insufficient-data-message"
            >
              {entriesNeeded > 0
                ? `Log ${entriesNeeded} more ${entriesNeeded === 1 ? 'visit' : 'visits'} to see patterns`
                : 'Keep logging to unlock more pattern insights'}
            </p>
          )}

          {dataQuality !== 'insufficient' && (
            <div className="flex flex-col gap-3">
              {similarDayInsights.map((match) => (
                <SimilarDayInsight key={match.waypointId} match={match} />
              ))}
              {bestConditions && <BestConditionsInsight summary={bestConditions} />}
              {locationComparison && <LocationComparisonInsight comparison={locationComparison} />}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
